import styled, { css } from "styled-components";

type CardType = "question" | "answer";

// question = the "black card" (licorice night), answer = the cream sticker card
const SKIN: Record<CardType, { bg: string; fg: string; border: string; lip: string }> = {
  question: {
    bg: "var(--night)",
    fg: "var(--night-ink)",
    border: "var(--night-lip)",
    lip: "var(--night-lip)",
  },
  answer: {
    bg: "var(--card)",
    fg: "var(--ink)",
    border: "var(--ink)",
    lip: "var(--line-strong)",
  },
};

const lifted = css<{ $type: CardType }>`
  box-shadow: 0 var(--lip) 0 ${({ $type }) => SKIN[$type].lip}, var(--shadow-float);
`;

export const CardContainer = styled.div<{
  $type: CardType;
  $handPosition?: number;
  $totalCards?: number;
  $open?: boolean;
  $selected?: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: var(--space-2);
  margin: auto;
  min-width: var(--card-size);
  max-width: var(--card-size);
  min-height: var(--card-size);
  padding: var(--space-3) var(--space-3) var(--space-2);
  background: ${({ $type }) => SKIN[$type].bg};
  color: ${({ $type }) => SKIN[$type].fg};
  border: var(--border-w) solid ${({ $type }) => SKIN[$type].border};
  border-radius: var(--radius-lg);
  box-shadow: 0 var(--lip) 0 ${({ $type }) => SKIN[$type].lip};
  font-family: var(--font-display);
  font-weight: 600;
  font-size: var(--card-font);
  line-height: 1.25;
  text-align: left;
  overflow-wrap: anywhere;
  user-select: none;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
  transition:
    translate var(--dur-slow) var(--ease-bounce),
    rotate var(--dur-slow) var(--ease-bounce),
    box-shadow var(--dur-base) var(--ease-out),
    border-color var(--dur-base);

  /* fan the hand: outer cards dip and tilt */
  translate: 0
    ${({ $handPosition, $totalCards, $open }) =>
      !$open && typeof $handPosition === "number" && $totalCards
        ? Math.abs($totalCards / 2 - ($handPosition + 0.5)) *
          Math.abs($totalCards / 2 - ($handPosition + 0.5)) *
          10
        : 0}px;
  rotate: ${({ $handPosition, $totalCards, $open, $selected }) =>
    typeof $handPosition === "number" &&
    !!$totalCards &&
    (!$open && !$selected
      ? `${(($handPosition + 0.5) / $totalCards) * 30 - 15}deg`
      : `${$handPosition + 0.5 < $totalCards / 2 ? "+" : "-"}360deg`)};

  ${({ $open }) => $open && lifted}

  ${({ $selected }) =>
    $selected &&
    css`
      translate: 0 -20%;
      border-color: var(--teal-shade);
      box-shadow: 0 var(--lip) 0 var(--teal-shade), var(--shadow-float);
    `}

  > div {
    user-select: none;
  }
`;

export const CardText = styled.div`
  white-space: pre-line;
`;

export const Blank = styled.span<{ $type: CardType }>`
  display: inline-block;
  width: 3em;
  height: 0.9em;
  margin: 0 0.15em;
  vertical-align: baseline;
  border-bottom: 3px solid
    ${({ $type }) => ($type === "question" ? "var(--gold)" : "var(--ink-faint)")};
  border-radius: 2px;
`;

export const CardFooter = styled.div<{ $type: CardType }>`
  display: flex;
  align-items: center;
  gap: var(--space-1);
  min-height: 22px;
  font-family: var(--font-body);
  font-size: var(--text-xs);
  font-weight: 800;
  opacity: 0.55;
  color: inherit;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const PlayerName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  max-width: 100%;
  padding: 1px var(--space-2);
  border-radius: var(--radius-full);
  background: var(--teal-tint);
  color: var(--teal-shade);
  border: var(--border-w) solid var(--teal);
  font-family: var(--font-body);
  font-size: var(--text-xs);
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  animation: pop-in var(--dur-slow) var(--ease-bounce) both;
`;

export const CardWrapper = styled.div<{
  $open?: boolean;
  $handPosition?: number;
  $totalCards?: number;
}>`
  overflow: visible;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  transition: translate var(--dur-slow) var(--ease-bounce);
  -webkit-tap-highlight-color: transparent;
  translate: ${({ $open, $handPosition, $totalCards }) =>
    $open
      ? `${
          typeof $handPosition === "number" && !!$totalCards
            ? ($totalCards / 2 - ($handPosition + 0.5)) * 100
            : 0
        }% -170%;`
      : "0% 0% !important"};
`;

export const ButtonsWrapper = styled.div<{
  $visible?: boolean;
}>`
  position: absolute;
  bottom: -64px;
  left: 50%;
  translate: -50% 0;
  z-index: 2;
  width: var(--card-size);
  display: flex;
  align-items: center;
  justify-content: space-around;
  scale: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: scale var(--dur-base) var(--ease-bounce);
`;

export const SelectedIcon = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(211, 245, 239, 0.85); /* --teal-tint, fallback */
  background: color-mix(in srgb, var(--teal-tint) 82%, transparent);

  span {
    display: grid;
    place-items: center;
    width: 64px;
    height: 64px;
    border-radius: var(--radius-full);
    background: var(--teal);
    color: var(--ink);
    border: var(--border-w) solid var(--ink);
    box-shadow: 0 var(--lip) 0 var(--teal-shade);
    animation: pop-in var(--dur-slow) var(--ease-bounce) both;
  }
`;
